import { Paper, debounce } from "@material-ui/core";
import { ChartArea, Chart as ChartJS, Plugin } from "chart.js";
import React, { useEffect, useRef, useState } from "react";
import { Line } from "react-chartjs-2";
import DraggableLineControl from "../components/DraggableLineControl";
import { useTrendingContext } from "../context/TrendingContext";
import { useGetVerticalLinePlugin } from "../hooks/useGetVerticalLinePlugin";
import { updateCursorData } from "../hooks/useUpdateCursorData";
import { useXAxisDatePlugin } from "../hooks/useXAxisDatePlugin";
import { useYAxisPlugin } from "../hooks/useYAxisPlugin";
import { LineCoords, ViewTag, ViewTagDataSet } from "../models/TrendingModels";
import { areRangesSimilar } from "../utils/areRangeSimilar";

interface Props {
	series: ViewTagDataSet[][];
	chartOptions: any;
	setChartOptions: any;
	setCursorData: any;
	chartData: any;
	setChartData: any;
}

export const TrendingChart = ({
	series,
	chartOptions,
	setChartOptions,
	chartData,
	setCursorData,
	setChartData,
}: Props) => {
	const {
		state,
		state: { viewTags },
		actions,
	} = useTrendingContext();

	const [lineTabCoords, setLineTabCoords] = useState<LineCoords | null>(null);
	const [chartArea, setChartArea] = useState<ChartArea | null>(null);

	const chartRef = useRef<ChartJS>(null);

	const forDebounce = useRef({
		viewTags,
		series,
		setChartOptions,
		setChartData,
	});
	const debouncedUpdateChart = useRef(
		debounce(() => {
			if (chartRef.current) {
				updateCursorData(chartRef.current, setCursorData);
				chartRef.current.update();
			}
		}, 250),
	);
	forDebounce.current = {
		viewTags,
		series,
		setChartOptions,
		setChartData,
	};

	const debouncedUpdateChartBasedOnData = useRef(
		debounce(() => {
			const { viewTags, series, setChartOptions, setChartData } =
				forDebounce.current;

			const axes: ViewTag[] = [];
			if (!series.length) {
				return;
			}
			Object.values(viewTags).forEach(({ viewTag: tag }, index: number) => {
				const existingAxis = axes.findIndex((existingTag: ViewTag) =>
					areRangesSimilar(existingTag, tag),
				);
				if (existingAxis === -1) {
					axes.push(tag); // Add new axis if no similar one exists
					seriesVsTags.current[tag.TagId] = tag.TagId;
				} else {
					seriesVsTags.current[tag.TagId] = axes[existingAxis].TagId;
				}
			});

			// Configure yAxes based on grouped axes
			const yAxesConfig = axes.map((axis) => {
				const min = Number(axis.MinScale);
				let max = Number(axis.MaxScale);
				if (!min && !max) {
					max = 1;
				}
				return {
					id: `y-axis-${axis.TagId}`,
					type: "linear" as const,
					display: axis.IsVisible,
					scaleLabel: {
						display: true,
						labelString: axis.TagName,
					},
					min: axis.IsAutoScale ? undefined : min,
					max: axis.IsAutoScale ? undefined : max,
					suggestedMin: axis.IsAutoScale ? undefined : min,
					suggestedMax: axis.IsAutoScale ? undefined : max,
					stepSize: 10,
					grid: {
						display: true,
					},
					afterUpdate(a: any) {
						const chart = a.chart;
						const keys = Object.keys(chart.scales).filter((sc) => sc !== "x");
						let counterOfDefaultsScales = 0;
						if (keys.length > 1) {
							for (const scaleKey in chart.scales) {
								const scale = chart.scales[scaleKey];
								if (scale.max === 1 && scale.min === 0) {
									(chart.scales[scaleKey] as any).display = false;
									(chart.scales[scaleKey] as any).options.display = false;

									counterOfDefaultsScales++;
								} else {
									(chart.scales[scaleKey] as any).display = true;
									(chart.scales[scaleKey] as any).options.display = true;
								}
								if (counterOfDefaultsScales === keys.length) {
									(chart.scales[keys[0]] as any).display = true;
									(chart.scales[keys[0]] as any).options.display = true;
								}
							}
						}
					},
				};
			});

			setChartOptions((prevOptions: any) => {
				const newResp = {
					...prevOptions,
					scales: {
						x: {
							...prevOptions.scales.x,
						},
						...yAxesConfig.reduce(
							(acc, cur) => ({ ...acc, [cur.id]: cur }),
							{},
						),
					},
				};

				return newResp as any;
			});

			const updatedSeries = series.map((serie, index) => {
				const tagsSortedArray = Object.values(viewTags).sort(
					(a, b) => b.order - a.order,
				);
				const tag = tagsSortedArray[index]?.viewTag;

				const yAxisTagId = seriesVsTags.current[tag?.TagId];

				return {
					yAxisID: `y-axis-${yAxisTagId}`,
					data: serie.map((sample: any) => ({
						x: new Date(sample.timestamp) as any,
						y: sample.value,
					})),
					tagId: tag?.TagId,
					borderColor: tag?.Color,
					hidden: !tag?.IsVisible,
					pointRadius: serie.length <= 50 ? 3 : 0,
				};
			});
			setChartShouldInitCursors(true);
			setChartData({
				labels: [],
				datasets: updatedSeries,
			});
		}, 1000),
	);

	const [chartShouldInitCursors, setChartShouldInitCursors] = useState(false);

	const verticalLinePlugin: Plugin = useGetVerticalLinePlugin(
		setChartArea,
		setLineTabCoords,
	);
	const xAxisDatePlugin: Plugin = useXAxisDatePlugin();
	const yAxisPlugin: Plugin = useYAxisPlugin();

	const seriesVsTags = useRef<{ [tagId: number]: number }>({});

	useEffect(() => {
		debouncedUpdateChartBasedOnData.current();
	}, [state.graphShouldUpdate, series]);

	useEffect(() => {
		if (chartShouldInitCursors) {
			if (chartRef.current) {
				updateCursorData(chartRef.current, setCursorData);
				chartRef.current.update();
			}
		}
	}, [chartShouldInitCursors, state.graphShouldUpdate]);

	return (
		<Paper style={{ height: "100%", position: "relative" }}>
			{lineTabCoords && chartArea ? (
				<DraggableLineControl
					color={"red"}
					chartArea={chartArea}
					initialLeft={lineTabCoords?.x1}
					otherLineCoord={lineTabCoords?.x2}
					onUpdate={(newLeft) => {
						setLineTabCoords((prevCoords: any) => {
							prevCoords.x1 = newLeft;
							return prevCoords;
						});
						// @ts-ignore
						chartRef.current!.mouseX = newLeft;
						debouncedUpdateChart.current();
					}}
				/>
			) : null}
			{lineTabCoords && chartArea ? (
				<DraggableLineControl
					color={"blue"}
					initialLeft={lineTabCoords?.x2}
					otherLineCoord={lineTabCoords?.x1}
					chartArea={chartArea}
					onUpdate={(newLeft) => {
						setLineTabCoords((prevCoords: any) => {
							prevCoords.x2 = newLeft;
							return prevCoords;
						});

						// @ts-ignore
						chartRef.current!.secondLineX = newLeft;

						debouncedUpdateChart.current();
					}}
				/>
			) : null}
			<Line
				ref={chartRef as any}
				data={chartData}
				options={chartOptions}
				plugins={[verticalLinePlugin, xAxisDatePlugin, yAxisPlugin]}
			/>
		</Paper>
	);
};
